// Whiteboard — the shop zebra, drawn in the Zebra logo's own manner: a solid
// black animal cut by white stripes, the spiky mane, the almond eye — and one
// brand-blue accent, a hard hat. Side on, facing right.
//
// One description (a small tree of SVG nodes), two outputs: React draws it on
// the board (ZebraArt.jsx), and the suite / preview renders it to a string.
// Every moving part is a group turning about its own joint; a POSE says how
// far each one turns, so a pose change is a smooth CSS transition and the
// gallop (and the tail's dog-like wag) is a CSS animation over the same groups.

export const BRAND_BLUE = '#0f93b6';
const INK = '#0b0c0e';
const FAR = '#2a2e35';

// pose: { part: [rotate°, dx, dy] } — parts not named stay as drawn (standing)
export const POSES = {
  standing: {},
  // sits like a dog: rump on the ground, hind legs folded under, front legs straight
  sitting: {
    all: [-24, 0, 31], neck: [14, 0, 0], tail: [58, 0, 0],
    hind: [-56, 0, 0], 'hind-shin': [168, 0, 0], 'hind-far': [-50, 0, 0], 'hind-far-shin': [164, 0, 0],
    front: [24, 0, 0], 'front-far': [21, 0, 0],
  },
};

const PIVOT = {
  all: [62, 96], neck: [142, 62], tail: [52, 62],
  hind: [76, 84], 'hind-far': [70, 82], front: [140, 86], 'front-far': [134, 84],
  'hind-shin': [70, 107], 'hind-far-shin': [64, 105], 'front-shin': [142, 109], 'front-far-shin': [136, 107],
};

const n = (tag, attrs = {}, children = []) => ({ tag, attrs, children });

// The hard hat on its own, facing left (the logo's zebra looks left), peak at
// the front; its box is 0..100 × 0..56 with the middle of its rim at (52, 50).
export const HARD_HAT = [
  { d: 'M16 46C16 20 32 6 54 6C76 6 90 20 90 46Z', fill: BRAND_BLUE },
  { d: 'M53 7V46', stroke: '#5cc3df', strokeWidth: 5 },
  { d: 'M2 48C8 44 14 45 20 46H93C97 46 99 48 99 50.5C99 53 97 54 93 54H18C8 54 3 52 2 48Z', fill: '#0b6f8b' },
];
// Where it sits on the logo's zebra head, in the logo artwork's own pixels
// (the head is the left 518 × 452 of the 1400 × 452 logo): the poll, tipped forward.
export const HAT_ON_LOGO = { cx: 214, cy: 84, w: 262, rot: -26 };
export const hatTransform = ({ cx, cy, w, rot } = HAT_ON_LOGO) => `translate(${cx} ${cy}) rotate(${rot}) scale(${w / 100}) translate(-52 -50)`;
// a group that turns about its joint, named so CSS (and a pose) can move it
const part = (name, pose, children) => ({ tag: 'g', part: name, pivot: PIVOT[name], pose: (pose && pose[name]) || [0, 0, 0], attrs: { className: `wbz-${name}` }, children });

const TORSO = 'M70 50C88 46 120 49 138 52C151 55 155 70 151 81C148 89 140 94 130 94C110 98 82 98 64 94C53 91 48 81 49 70C50 59 57 52 70 50Z';
const NECK = 'M133 57C138 45 149 29 161 20L176 23C181 31 177 42 171 49C163 59 157 71 151 83C147 74 140 65 133 57Z';
const HEAD = 'M159 18C167 10 181 11 188 19L200 39C204 45 203 53 195 55L183 54C175 52 168 46 166 40C162 32 159 26 159 18Z';
const MANE = 'M133 55L122 50L131 46L121 38L133 39L126 29L138 32L134 20L145 27L144 14L153 22L155 8L161 19L164 6L168 20Z';

function leg(name, hip, knee, hoof, far) {
  const ink = far ? FAR : INK;
  const band = (a, b, t) => { const x = a[0] + (b[0] - a[0]) * t; const y = a[1] + (b[1] - a[1]) * t; return n('path', { d: `M${x - 3.2} ${y}h6.4` }); };
  return { name, hip, knee, hoof, ink, band };
}
function legNode(L, pose) {
  return part(L.name, pose, [
    n('path', { d: `M${L.hip[0]} ${L.hip[1]}L${L.knee[0]} ${L.knee[1]}`, stroke: L.ink, strokeWidth: 9.5, strokeLinecap: 'round' }),
    n('g', { stroke: '#fff', strokeWidth: 1.7, strokeLinecap: 'round' }, [L.band(L.hip, L.knee, 0.4), L.band(L.hip, L.knee, 0.72)]),
    part(`${L.name}-shin`, pose, [
      n('path', { d: `M${L.knee[0]} ${L.knee[1]}L${L.hoof[0]} ${L.hoof[1]}`, stroke: L.ink, strokeWidth: 6.6, strokeLinecap: 'round' }),
      n('g', { stroke: '#fff', strokeWidth: 1.5, strokeLinecap: 'round' }, [L.band(L.knee, L.hoof, 0.3), L.band(L.knee, L.hoof, 0.58)]),
      n('path', { d: `M${L.hoof[0] - 4} ${L.hoof[1] - 1}h8l1 4h-10z`, fill: L.ink }),
    ]),
  ]);
}

// opts: { id (unique per drawing, for the clip paths), pose: 'standing' | 'sitting', running }
export function zebraTree({ id = 'z', pose: poseName = 'standing', running = false } = {}) {
  const pose = POSES[running ? 'standing' : poseName] || POSES.standing;
  const clip = (k) => `url(#wbz-${k}-${id})`;
  const white = { stroke: '#fff', strokeLinecap: 'round', fill: 'none' };
  const legs = {
    hindFar: leg('hind-far', [70, 82], [64, 105], [68, 128], true),
    frontFar: leg('front-far', [134, 84], [136, 107], [135, 128], true),
    hind: leg('hind', [76, 84], [70, 107], [74, 130]),
    front: leg('front', [140, 86], [142, 109], [141, 130]),
  };
  return n('svg', { viewBox: '0 -18 214 160', className: `wbz ${running ? 'is-running' : `is-${poseName}`}` }, [
    n('defs', {}, [
      n('clipPath', { id: `wbz-torso-${id}` }, [n('path', { d: TORSO })]),
      n('clipPath', { id: `wbz-neck-${id}` }, [n('path', { d: NECK })]),
      n('clipPath', { id: `wbz-head-${id}` }, [n('path', { d: HEAD })]),
    ]),
    n('ellipse', { className: 'wbz-shadow', cx: 100, cy: 133, rx: 62, ry: 4, fill: '#0f172a', opacity: 0.12 }),
    part('all', pose, [
      legNode(legs.hindFar, pose),
      legNode(legs.frontFar, pose),
      part('tail', pose, [
        n('path', { d: 'M52 62C42 66 37 78 36 92', stroke: INK, strokeWidth: 2.6, strokeLinecap: 'round', fill: 'none' }),
        n('path', { d: 'M36 88C31 94 32 104 37 107C41 102 41 95 38 89Z', fill: INK }),
      ]),
      n('path', { d: TORSO, fill: INK }),
      n('g', { ...white, strokeWidth: 3.4, clipPath: clip('torso') }, [
        n('path', { d: 'M66 44C61 60 60 80 66 100' }), n('path', { d: 'M78 44C74 60 73 80 79 100' }),
        n('path', { d: 'M90 44C87 60 86 80 91 100' }), n('path', { d: 'M102 46C100 62 100 80 104 100' }),
        n('path', { d: 'M114 46C113 62 114 80 117 100' }), n('path', { d: 'M126 47C126 62 128 78 131 98' }),
        n('path', { d: 'M48 70C56 66 64 68 70 74' }), n('path', { d: 'M48 80C56 77 63 80 67 86' }),
      ]),
      part('neck', pose, [
        n('path', { d: MANE, fill: INK }),
        n('path', { d: NECK, fill: INK }),
        n('g', { ...white, strokeWidth: 3, clipPath: clip('neck') }, [
          n('path', { d: 'M124 58Q146 64 164 77' }), n('path', { d: 'M130 47Q151 54 170 66' }),
          n('path', { d: 'M136 37Q156 44 175 55' }), n('path', { d: 'M142 27Q162 34 181 44' }),
        ]),
        n('path', { d: 'M167 14L162 -1L176 10Z', fill: INK }),
        n('path', { d: 'M166.5 10.5L164.6 3.4', stroke: '#fff', strokeWidth: 1.2, strokeLinecap: 'round' }),
        n('path', { d: HEAD, fill: INK }),
        n('g', { ...white, strokeWidth: 2.5, clipPath: clip('head') }, [
          n('path', { d: 'M170 13Q165 25 162 37' }), n('path', { d: 'M172 35Q181 32 190 37' }),
          n('path', { d: 'M193 25Q186 36 182 47' }), n('path', { d: 'M199 34Q193 42 189 51' }),
        ]),
        n('path', { d: 'M174 25C178 20.4 184 20.4 188 25C184 29.4 178 29.4 174 25Z', fill: '#fff' }),
        n('path', { d: 'M177.4 25C179.6 22.8 182.6 22.8 184.8 25C182.6 27.2 179.6 27.2 177.4 25Z', fill: INK, className: 'wbz-pupil' }),
        // the hard hat: flat, in the brand's blue
        n('path', { d: 'M165 14C165 3.6 177.6 -1 186 5.2C189.2 7.8 190.4 10.8 190.4 14Z', fill: BRAND_BLUE }),
        n('path', { d: 'M176.6 1.4V14', stroke: '#5cc3df', strokeWidth: 1.6 }),
        n('path', { d: 'M160.6 14.6H194', stroke: '#0b6f8b', strokeWidth: 3.2, strokeLinecap: 'round' }),
      ]),
      legNode(legs.hind, pose),
      legNode(legs.front, pose),
    ]),
    running ? n('g', { className: 'wbz-dust', fill: '#cbd5e1' }, [
      n('circle', { className: 'wbz-puff p1', cx: 56, cy: 128, r: 4.6 }), n('circle', { className: 'wbz-puff p2', cx: 44, cy: 124, r: 3.8 }), n('circle', { className: 'wbz-puff p3', cx: 34, cy: 129, r: 3.2 }),
    ]) : null,
  ].filter(Boolean));
}

// ---- to an SVG string (the suite, previews): poses as transform attributes
const kebab = (k) => (k === 'className' ? 'class' : k === 'viewBox' ? k : k.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`));
const esc = (v) => String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
export function zebraSvg(opts = {}, width = 214) {
  const walk = (node) => {
    if (node == null) return '';
    if (typeof node === 'string') return esc(node);
    const attrs = { ...node.attrs };
    if (node.part) {
      const [r, x, y] = node.pose;
      if (r || x || y) attrs.transform = `translate(${x} ${y}) rotate(${r} ${node.pivot[0]} ${node.pivot[1]})`;
    }
    if (node.tag === 'svg') { attrs.xmlns = 'http://www.w3.org/2000/svg'; attrs.width = width; attrs.height = Math.round((width * 160) / 214); }
    const a = Object.entries(attrs).map(([k, v]) => ` ${kebab(k)}="${esc(v)}"`).join('');
    return `<${node.tag}${a}>${(node.children || []).map(walk).join('')}</${node.tag}>`;
  };
  return walk(zebraTree(opts));
}
